import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { withStyles } from '@material-ui/core/styles';
import FormControl from '@material-ui/core/FormControl';
import ReactDropzone from 'react-dropzone';
import FormHelperText from "@material-ui/core/FormHelperText";
import InputBase from '@material-ui/core/InputBase';
import CloudUpload from '@material-ui/icons/CloudUpload';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import DescriptionIcon from '@material-ui/icons/Description';
import DeleteIcon from '@material-ui/icons/Delete';
import Check from '@material-ui/icons/Check';
import Avatar from '@material-ui/core/Avatar';
import LinearProgress from '@material-ui/core/LinearProgress';
import Grid from '@material-ui/core/Grid';
import Card from '@material-ui/core/Card';
import Typography from '@material-ui/core/Typography';
import { lightGreen } from '@material-ui/core/colors';
import IconButton from '@material-ui/core/IconButton';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import Box from '@material-ui/core/Box';
import Button from 'components/common/Button';
import { makeStyles } from '@material-ui/core/styles';

const styles = (theme, props) => {
  return {
    root: {
      display: 'flex',
      flexWrap: 'wrap',
      marginTop: theme.spacing(2),
    },
    label: {
      marginBottom: theme.spacing(1),
    },
    rowItem: {
      marginBottom: 8,
    },
    cardItem: {},
    bootstrapFormLabel: {
      color: theme.palette.grey[700],
    },
    uploading: {
      color: theme.palette.grey[500],
    },
    zone: {
      'backgroundColor': theme.palette.background.default,
      'display': 'flex',
      'justifyContent': 'center',
      'alignItems': 'center',
      'flexDirection': 'column',
      'borderRadius': '.1875rem',
      'border': '2px dashed #ececee',
      'padding': '1.5rem',
      '&:focus': {
        outline: 'none',
      },
    },
    zoneCards: {
      justifyContent: 'flex-start',
      alignItems: 'space-between',
    },
    placeHolder: {
      margin: 0,
      marginTop: 12,
    },
    icon: {
      width: '2em',
      height: '2em',
    },
    preview: {
      width: '3em',
      height: '3em',
      marginRight: 8,
    },
    imageName: {
      marginRight: 8,
    },
    checkIcon: {
      color: lightGreen[500],
    },
    deleteIcon: {
      cursor: 'pointer',
    },
    card: {
      'width': '30%',
      'maxWidth': 350,
      'flexGrow': 1,
      'height': 200,
      'flexShrink': 0,
      'overflow': 'hidden',
      'border': '1px solid #eee',
      'boxShadow': 'none',
      '& .image': {
        'width': '100%',
        'height': '80%',
        '& img': {
          objectFit: 'contain',
        },
      },
      '& .footer': {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        backgroundColor: 'white',
        padding: 10,
      },
    },
  };
};

const useStyles = makeStyles({
  root: {
    textAlign: 'center',
  },
});

const Screenshot = ({ onClose, url }) => {
  const classes = useStyles();

  return (
    <Dialog onClick={(e) => e.stopPropagation()} open fullWidth maxWidth="md">
      <DialogContent
        classes={{
          root: classes.root,
        }}
      >
        <img src={url} alt="result-screenshot" />
        <Box
          textAlign="right"
          paddingTop={3}
          paddingBottom={3}
          onClick={onClose}
        >
          <Button variant="contained" color="primary">
            Close
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

const PDFViewer = ({ onClose, url }) => {
  const classes = useStyles();

  return (
    <Dialog onClick={(e) => e.stopPropagation()} open maxWidth="md">
      <DialogContent
        classes={{
          root: classes.root,
        }}
      >
        <object width="800px" height="600px" data={url}></object>
        <Box
          textAlign="right"
          paddingTop={3}
          paddingBottom={3}
          onClick={onClose}
        >
          <Button variant="contained" color="primary">
            Close
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

class CustomDropZone extends PureComponent {
  constructor(props) {
    super(props);
    this.state = { files: {}, showDetails: false, showPDF: false };
  }

  componentDidMount() {
    this.setFilesFromProps(this.props.attachments);
  }

  componentWillReceiveProps(nextProps, nextContext) {
    if (
      (!this.props.attachments || !this.props.attachments.length) &&
      nextProps.attachments &&
      nextProps.attachments.length
    ) {
      this.setFilesFromProps(nextProps.attachments);
    }
  }

  setFilesFromProps(attachments = []) {
    let files = {};

    attachments.forEach((attachment) => {
      const name = attachment.name || attachment.fileName;
      if (name) {
        files[name] = {
          name,
          ...attachment,
          progress: 100,
        };
      }
    });

    this.setState({
      files,
    });
  }

  onDrop = (files) => {
    files.forEach((file) => {
      this.setState(
        (prevState) => ({
          files: {
            ...(this.props.multiple ? prevState.files : {}),
            [file.name]: {
              ...prevState.files[file.name],
              file,
              name: file.name,
              type: file.type,
              url: null,
              progress: 0,
            },
          },
        }),
        () => {
          this.props.onUpload(
            Object.values(this.state.files).map((f) => f.file),
            ({ percent }, file) => {
              // progress
              this.setState({
                files: {
                  ...this.state.files,
                  [file.name]: {
                    ...this.state.files[file.name],
                    progress: percent,
                  },
                },
              });
            },
            (url, file) => {
              // success
              this.setState({
                files: {
                  ...this.state.files,
                  [file.name]: {
                    ...this.state.files[file.name],
                    url,
                  },
                },
              });
            },
            (error, file) => {
              this.setState({
                files: {
                  ...this.state.files,
                  [file.name]: {
                    ...this.state.files[file.name],
                    error: error.message,
                  },
                },
              });
            },
          );
          if (this.props.onChange) {
            this.props.onChange(Object.values(this.state.files));
          }
        },
      );
    });
  };

  onDelete = (e, file) => {
    e.stopPropagation();
    this.props.onRemove && this.props.onRemove(file);
    const { files } = this.state;
    delete files[file.name];
    this.setState({
      files: {
        ...files,
      },
    });
    if (this.props.onChange) {
      this.props.onChange(Object.values(files));
    }
  };

  renderCard = (data, isDownloadComplete) => {
    const { classes } = this.props;
    const { showDetails, showPDF } = this.state;
    return (
      <Card raised={false} elevation={0} className={classes.card}>
        <Avatar
          variant="square"
          src={data.url || data.name}
          className="image"
        />
        {isDownloadComplete ? (
          <div className="footer">
            <Box display="flex">
              <Check size={20} className={classes.checkIcon} />
              {}

              {data?.file?.type === 'application/pdf' ||
              data?.name.split('.').pop() === 'pdf' ? (
                <IconButton
                  size="small"
                  style={{ marginRight: 'auto' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    this.setState({ showPDF: true });
                  }}
                >
                  {' '}
                  <OpenInNewIcon fontSize="small" color="action" />
                </IconButton>
              ) : (
                <IconButton
                  size="small"
                  style={{ marginRight: 'auto' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    this.setState({ showDetails: true });
                  }}
                >
                  {' '}
                  <OpenInNewIcon fontSize="small" color="action" />
                </IconButton>
              )}
            </Box>
            <DeleteIcon
              onClick={(e) => this.onDelete(e, data)}
              color="error"
              classes={{ root: classes.deleteIcon }}
            />
          </div>
        ) : (
          <LinearProgress variant={'determinate'} value={data.progress} />
        )}
        {showDetails && (
          <Screenshot
            url={data.url || data.name}
            onClose={(e) => {
              e.stopPropagation();
              this.setState({ showDetails: false });
            }}
          />
        )}
        {showPDF && (
          <PDFViewer
            url={data.url || data.name}
            onClose={(e) => {
              e.stopPropagation();
              this.setState({ showPDF: false });
            }}
          />
        )}
      </Card>
    );
  };

  render() {
    const {
      classes,
      id,
      label,
      multiple,
      accept,
      variant,
      color,
      placeholder,
      listType,
      hasError,
      helpText,
      ...otherProps
    } = this.props;
    const { files } = this.state;
    const uploadedFiles = Object.values(files);

    return (
      <div className={classes.root}>
        <FormControl fullWidth>
          <Typography className={classes.label} component="label" variant={variant || "caption"} color={color || "textPrimary"}>
            {label}
          </Typography>
          <ReactDropzone
            id={id}
            multiple={multiple}
            accept={accept}
            onDrop={this.onDrop}
            {...otherProps}
          >
            {({ getRootProps, getInputProps, isDragActive, isDragReject }) => {
              return (
                <div
                  {...getRootProps()}
                  className={cx(classes.zone, {
                    'dropzone--isActive': isDragActive,
                    [classes.zoneCards]: listType === 'card',
                  })}
                >
                  <InputBase inputProps={getInputProps()} />
                  {!isDragActive && !uploadedFiles.length && (
                    <>
                      <CloudUpload
                        className={classes.icon}
                        color={'disabled'}
                      />
                      <Typography
                        component="p"
                        variant="caption"
                        color="textSecondary"
                        className={classes.placeHolder}
                      >
                        {placeholder || 'Drag and Drop or Browse'}
                      </Typography>
                    </>
                  )}
                  {isDragReject && (
                    <p className={classes.placeHolder}>
                      Unsupported file type...
                    </p>
                  )}
                  {uploadedFiles.map((data) => {
                    const isDownloadComplete = data.progress === 100;
                    if (listType === 'card')
                      return this.renderCard(data, isDownloadComplete);
                    return (
                      <Grid
                        container
                        alignItems={'center'}
                        key={data.name}
                        className={classes.rowItem}
                      >
                        {
                          <DescriptionIcon
                            fontSize="small"
                            className={cx(
                              classes.icon,
                              !isDownloadComplete && classes.uploading,
                            )}
                            color={isDownloadComplete ? 'primary' : 'inherit'}
                          />
                        }
                        <Grid item xs={true}>
                          <Grid container justify={'space-between'}>
                            <Grid item>
                              <Grid container>
                                <Typography
                                  component={'span'}
                                  variant="body2"
                                  className={classes.imageName}
                                >
                                  {data.name}
                                </Typography>
                              </Grid>
                            </Grid>
                            <Grid item className={'align-content-xs-center'}>
                              <DeleteIcon
                                onClick={(e) => this.onDelete(e, data)}
                                color="error"
                                classes={{ root: classes.deleteIcon }}
                              />
                            </Grid>
                          </Grid>
                          <LinearProgress
                            variant={'determinate'}
                            value={data.progress}
                          />
                        </Grid>
                      </Grid>
                    );
                  })}
                </div>
              );
            }}
          </ReactDropzone>
          {hasError && <FormHelperText error>{helpText}</FormHelperText>}
        </FormControl>
      </div>
    );
  }
}

CustomDropZone.propTypes = {
  classes: PropTypes.object.isRequired,
  id: PropTypes.string,
  onUpload: PropTypes.func,
  multiple: PropTypes.bool,
  onRemove: PropTypes.func,
  accept: PropTypes.string,
  listType: PropTypes.oneOf(['card', 'list']),
  attachments: PropTypes.array,
};

CustomDropZone.defaultProps = {
  id: 'editor',
  onUpload: () => {},
  multiple: false,
  accept: 'image/*',
  listType: 'list',
  attachments: [],
};

export default withStyles(styles)(CustomDropZone);
